import { useState, useEffect } from 'react'
import { Form, Input, Select, Button, Row, Col, Card } from 'antd'
import { SearchOutlined, ClearOutlined } from '@ant-design/icons'
import { SKUFilter } from '../../types'
import skuService from '../../services/sku'

interface SKUFilterProps {
  onFilter: (filter: SKUFilter) => void
  loading?: boolean
}

const SKUFilterComponent: React.FC<SKUFilterProps> = ({ onFilter, loading = false }) => {
  const [form] = Form.useForm()
  const [colorCodes, setColorCodes] = useState<string[]>([])
  const [sizes, setSizes] = useState<string[]>([])

  useEffect(() => {
    // 加载筛选选项
    loadFilterOptions()
  }, [])

  const loadFilterOptions = async () => {
    try {
      const [colorCodesData, sizesData] = await Promise.all([
        skuService.getUniqueColorCodes(),
        skuService.getUniqueSizes()
      ])
      setColorCodes(colorCodesData)
      setSizes(sizesData)
    } catch (error) {
      // 错误处理已在service层处理
    }
  }

  const handleFilter = (values: any) => {
    // 过滤掉空值
    const filter: SKUFilter = {}
    if (values.barcode?.trim()) filter.barcode = values.barcode.trim()
    if (values.styleNumber?.trim()) filter.styleNumber = values.styleNumber.trim()
    
    // 处理颜色代码（可能是数组或字符串）
    if (values.colorCode) {
      filter.colorCode = Array.isArray(values.colorCode) 
        ? values.colorCode[0] 
        : values.colorCode
    }
    
    // 处理尺码（可能是数组或字符串）
    if (values.size) {
      filter.size = Array.isArray(values.size) 
        ? values.size[0] 
        : values.size
    }

    onFilter(filter)
  }

  const handleReset = () => {
    form.resetFields()
    onFilter({})
  }

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFilter}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item label="条形码" name="barcode">
              <Input placeholder="请输入条形码" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="款号" name="styleNumber">
              <Input placeholder="请输入款号" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="颜色代码" name="colorCode">
              <Select
                placeholder="请输入或选择颜色代码"
                allowClear
                showSearch
                mode="tags"
                maxTagCount={1}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={colorCodes.map(code => ({ label: code, value: code }))}
                onChange={(value) => {
                  // 如果是数组，只取第一个值
                  if (Array.isArray(value) && value.length > 0) {
                    form.setFieldValue('colorCode', value[0])
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="尺码" name="size">
              <Select
                placeholder="请输入或选择尺码"
                allowClear
                showSearch
                mode="tags"
                maxTagCount={1}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={sizes.map(size => ({ label: size, value: size }))}
                onChange={(value) => {
                  // 如果是数组，只取第一个值
                  if (Array.isArray(value) && value.length > 0) {
                    form.setFieldValue('size', value[0])
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
              style={{ marginRight: 8 }}
            >
              搜索
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleReset}
            >
              重置
            </Button>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}

export default SKUFilterComponent